"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ScreenShotManager = void 0;
const three_1 = require("three");
const types_1 = require("~/store/types");
const utils_1 = require("~/utils/utils");
const OBB_js_1 = require("three/examples/jsm/math/OBB.js");
const utils_3d_1 = require("../../utils/utils.3d");
const consts_1 = require("../../consts");
const DimensionLeaderGenerator_1 = require("./DimensionLeaderGenerator");
const LightsHelper_1 = __importDefault(require("../LightsHelper"));
const IMAGE_BLOB_TYPE = 'image/jpeg';
const DP_CO_ORDINATE_SYSTEM = {
    X: consts_1.consts.DIRECTIONS_LIST[consts_1.consts.AXES.positiveX].clone(),
    Y: consts_1.consts.DIRECTIONS_LIST[consts_1.consts.AXES.negativeZ].clone(),
    Z: consts_1.consts.DIRECTIONS_LIST[consts_1.consts.AXES.positiveY].clone(),
};
const DP_THREE_CO_ORDINATE_SYSTEM = Object.assign(Object.assign({}, DP_CO_ORDINATE_SYSTEM), { Z: DP_CO_ORDINATE_SYSTEM.Z.clone().negate() });
const DP_THREE_SCENE_TO_THREE_SCENE_TRANSFORM = new three_1.Matrix4();
DP_THREE_SCENE_TO_THREE_SCENE_TRANSFORM.set(DP_THREE_CO_ORDINATE_SYSTEM.X.x, DP_THREE_CO_ORDINATE_SYSTEM.Y.x, DP_THREE_CO_ORDINATE_SYSTEM.Z.x, 0, DP_THREE_CO_ORDINATE_SYSTEM.X.y, DP_THREE_CO_ORDINATE_SYSTEM.Y.y, DP_THREE_CO_ORDINATE_SYSTEM.Z.y, 0, DP_THREE_CO_ORDINATE_SYSTEM.X.z, DP_THREE_CO_ORDINATE_SYSTEM.Y.z, DP_THREE_CO_ORDINATE_SYSTEM.Z.z, 0, 0, 0, 0, 1);
class ScreenShotManager {
    constructor(viewer) {
        this.viewer = viewer;
        this.scene = viewer.getScene();
        this.dimensionLeaderGenerator = new DimensionLeaderGenerator_1.DimensionLeaderGenerator();
        // With the lighting configuration used on 3D scene, parts look good when the camera is positioned at front top right corner
        // But for taking screen shots of different views the camera is set at different positions
        // Hence we need to adjust the lighting configuration on the scene as per camera position for parts on scene to look good
        const lightsHelper = new LightsHelper_1.default();
        const { ambient, directionalOnRight, directionalOnLeft, directionalOnFront, directionalOnBack } = lightsHelper.lightsConfiguration;
        this.lights = [ambient, directionalOnRight, directionalOnLeft, directionalOnFront, directionalOnBack];
    }
    takePanelScreenShots(bom) {
        var _a, _b, _c, _d, _e;
        return __awaiter(this, void 0, void 0, function* () {
            const panelScreenShots = [];
            // Create scene panel map for quick access
            const panelsOnScene = new Map(this.viewer.getPanels().map((panel) => [panel.uuid, panel.clone(true)]));
            if (!(panelsOnScene === null || panelsOnScene === void 0 ? void 0 : panelsOnScene.size)) {
                return panelScreenShots;
            }
            // Get item numbers of panels in the bom in the order they appear in bom
            const bomPanelsItems = [];
            const panelDetailsDictionary = new Map();
            const extrusionsCount = (_c = (_b = (_a = bom === null || bom === void 0 ? void 0 : bom.groups) === null || _a === void 0 ? void 0 : _a.find((group) => group.name.toLowerCase() === 'extrusions')) === null || _b === void 0 ? void 0 : _b.items.length) !== null && _c !== void 0 ? _c : 0;
            (_e = (_d = bom === null || bom === void 0 ? void 0 : bom.groups) === null || _d === void 0 ? void 0 : _d.find((group) => group.name.toLowerCase() === 'components')) === null || _e === void 0 ? void 0 : _e.items.forEach((item, index) => {
                var _a, _b;
                if (((_a = item.componentType) === null || _a === void 0 ? void 0 : _a.name) === types_1.COMPONENT_TYPE.PANEL) {
                    bomPanelsItems.push(item);
                    if ((_b = item.uuid) === null || _b === void 0 ? void 0 : _b.length) {
                        panelDetailsDictionary.set(item.uuid[0], {
                            quantity: item.quantity,
                            orderNumber: extrusionsCount + index + 1,
                        });
                    }
                }
            });
            if (!(bomPanelsItems === null || bomPanelsItems === void 0 ? void 0 : bomPanelsItems.length)) {
                return panelScreenShots;
            }
            // Prepare list of panels for taking screenshots
            const panelDetailsForScreenShotsList = [];
            let longestPanelSideLength = 0;
            bomPanelsItems.forEach((bomPanel) => {
                var _a, _b;
                const panelOnScene = bomPanel.uuid && panelsOnScene.get(bomPanel.uuid[0]);
                if (panelOnScene) {
                    // Update longest panel side
                    const panelBoundingBox = (0, utils_3d_1.getAABBox)(panelOnScene);
                    const currentPanelSize = panelBoundingBox.getSize(new three_1.Vector3());
                    longestPanelSideLength = (0, utils_3d_1.largest)([
                        longestPanelSideLength,
                        currentPanelSize.x,
                        currentPanelSize.y,
                        currentPanelSize.z,
                    ]);
                    const panelDetail = bomPanel.uuid && panelDetailsDictionary.get(bomPanel.uuid[0]);
                    panelDetailsForScreenShotsList.push({
                        panel: panelOnScene,
                        description: bomPanel.description,
                        quantity: (_a = panelDetail === null || panelDetail === void 0 ? void 0 : panelDetail.quantity) !== null && _a !== void 0 ? _a : 1,
                        orderNumber: (_b = panelDetail === null || panelDetail === void 0 ? void 0 : panelDetail.orderNumber) !== null && _b !== void 0 ? _b : 0,
                    });
                }
            });
            // eslint-disable-next-line no-restricted-syntax
            for (const panelDetailsForScreenShot of panelDetailsForScreenShotsList) {
                panelScreenShots.push(
                // eslint-disable-next-line no-await-in-loop
                yield this.takePanelScreenShot(panelDetailsForScreenShot.panel, panelDetailsForScreenShot.description, panelDetailsForScreenShot.quantity, panelDetailsForScreenShot.orderNumber, longestPanelSideLength));
            }
            return panelScreenShots;
        });
    }
    takePanelScreenShot(panel, description, quantity, orderNumber, longestPanelSideLength, size = consts_1.consts.SCREENSHOT_SIDE_LENGTH_FOR_PANELS_PX) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            // Set panel at center
            panel.rotate(panel.quaternion.clone().invert());
            const panelBoundingBox = (0, utils_3d_1.getAABBox)(panel);
            const panelCenter = panelBoundingBox.getCenter(new three_1.Vector3());
            const panelSize = panelBoundingBox.getSize(new three_1.Vector3());
            panel.translate(panelCenter.clone().multiplyScalar(-1));
            // Set panel bottom to match the viewport bottom w.r.t. longest panel side out of all panels
            // Set up camera up vector simultaneously relative to longer side of the panel
            const xLength = panelSize.x;
            const yLength = panelSize.y;
            let longerSideLength = yLength;
            const cameraUp = consts_1.consts.DIRECTIONS_LIST.Y.clone();
            if ((0, utils_3d_1.greater)(xLength, yLength)) {
                panel.rotateZ(Math.PI / 2);
                longerSideLength = xLength;
            }
            // Panels are to be rendered with just color
            // eslint-disable-next-line no-param-reassign
            panel.mesh.material = new three_1.MeshBasicMaterial({
                color: (_a = panel.baseColor) === null || _a === void 0 ? void 0 : _a.clone(),
            });
            const scene = new three_1.Scene();
            scene.add(panel);
            const currentPanelToLargerPanelScale = longerSideLength / longestPanelSideLength;
            const screenShotBackgroundColor = ((_b = panel.baseColor) === null || _b === void 0 ? void 0 : _b.getHex()) === 0xffffff ? consts_1.consts.SCREENSHOT_BACKGROUND_COLOR_GRAY : new three_1.Color(consts_1.consts.WHITE_COLOR);
            const screenShotBlobWithDimensions = yield this.takeOrthogonalScreenShot(scene, consts_1.consts.DIRECTIONS_LIST['-Z'].clone(), cameraUp, {
                size,
                extraScaleFactor: currentPanelToLargerPanelScale,
                addDimensions: true,
                backgroundColor: screenShotBackgroundColor,
            });
            return {
                id: panel.partDetails.itemNumber,
                blob: screenShotBlobWithDimensions,
                description,
                quantity,
                orderNumber,
            };
        });
    }
    takeProjectsDimensionsScreenShots(size) {
        return __awaiter(this, void 0, void 0, function* () {
            // Prepare copy of scene with items that should be part of the screen shot in order to calculate correct bounding box
            const assemblyScene = new three_1.Scene();
            assemblyScene.matrixAutoUpdate = false;
            assemblyScene.matrix = DP_THREE_SCENE_TO_THREE_SCENE_TRANSFORM.clone();
            this.scene.children.forEach((child) => {
                if (!(0, utils_3d_1.isBaseObject3D)(child)) {
                    return;
                }
                if (this.viewer.isObjectUnderInsertion(child)) {
                    return;
                }
                const childCopy = child.clone();
                if ((0, utils_3d_1.isPanel)(childCopy)) {
                    childCopy.setTransparency(true, 0.5);
                }
                assemblyScene.add(childCopy);
            });
            // Find longest scene dimension to find relative scale factor
            const sceneAABB = new three_1.Box3().setFromObject(assemblyScene);
            const sceneSize = sceneAABB.getSize(new three_1.Vector3());
            const longestDimension = (0, utils_3d_1.largest)(sceneSize.toArray());
            // Take scene screen shots from three sides
            const topFaceToLargestFaceScale = (0, utils_3d_1.largest)([sceneSize.x, sceneSize.z]) / longestDimension;
            const topScreenShot = yield this.takeOrthogonalScreenShot(assemblyScene, consts_1.consts.DIRECTIONS_LIST.Y.clone(), consts_1.consts.DIRECTIONS_LIST.Z.clone(), {
                size,
                extraScaleFactor: topFaceToLargestFaceScale,
                addLights: true,
                addDimensions: true,
                convertedFromDPSceneToThreeScene: true,
            });
            const frontFaceToLargestFaceScale = (0, utils_3d_1.largest)([sceneSize.x, sceneSize.y]) / longestDimension;
            const frontScreenShot = yield this.takeOrthogonalScreenShot(assemblyScene, consts_1.consts.DIRECTIONS_LIST['-Z'].clone(), consts_1.consts.DIRECTIONS_LIST.Y.clone(), {
                size,
                extraScaleFactor: frontFaceToLargestFaceScale,
                addLights: true,
                addDimensions: true,
                convertedFromDPSceneToThreeScene: true,
            });
            const rightFaceToLargestFaceScale = (0, utils_3d_1.largest)([sceneSize.z, sceneSize.y]) / longestDimension;
            const rightScreenShot = yield this.takeOrthogonalScreenShot(assemblyScene, consts_1.consts.DIRECTIONS_LIST.X.clone(), consts_1.consts.DIRECTIONS_LIST.Y.clone(), {
                size,
                extraScaleFactor: rightFaceToLargestFaceScale,
                addLights: true,
                addDimensions: true,
                convertedFromDPSceneToThreeScene: true,
            });
            const isometricScreenShot = yield this.takeOrthogonalScreenShot(assemblyScene, consts_1.consts.UNIT_VECTOR_FRONT_TOP_RIGHT.clone(), consts_1.consts.UNIT_VECTOR_BACK_TOP_LEFT.clone(), {
                size,
                addLights: true,
                convertedFromDPSceneToThreeScene: true,
                isIsometric: true,
            });
            const projectsDimensionsScreenShots = {
                top: topScreenShot,
                front: frontScreenShot,
                right: rightScreenShot,
                isometric: isometricScreenShot,
            };
            return projectsDimensionsScreenShots;
        });
    }
    prepareSceneImageForOrthogonalScreenShot(scene, faceNormal, cameraUp, settings) {
        var _a, _b, _c;
        return __awaiter(this, void 0, void 0, function* () {
            const backgroundColor = (_a = settings.backgroundColor) !== null && _a !== void 0 ? _a : new three_1.Color(consts_1.consts.WHITE_COLOR);
            const screenShotSize = (_b = settings.size) !== null && _b !== void 0 ? _b : consts_1.consts.SCREENSHOT_SIDE_LENGTH_PX;
            const { addLights, isIsometric } = settings;
            const { sceneDimensions, sceneCenter, cameraDistance } = ScreenShotManager.getOrthographicScreenShotConfig(scene, faceNormal, cameraUp, isIsometric);
            const sceneDimensionX = sceneDimensions.x;
            const sceneDimensionY = sceneDimensions.y;
            const longerDimension = (0, utils_3d_1.largest)([sceneDimensionX, sceneDimensionY]);
            const sceneWidth = (screenShotSize * sceneDimensionX) / longerDimension;
            const sceneHeight = (screenShotSize * sceneDimensionY) / longerDimension;
            // Set up renderer
            const canvas = document.createElement('canvas');
            const renderer = new three_1.WebGLRenderer({
                antialias: true,
                canvas,
            });
            renderer.setSize(sceneWidth, sceneHeight);
            renderer.setClearColor(backgroundColor);
            // Set up camera
            const halfCameraWidth = sceneDimensionX / 2;
            const halfCameraHeight = sceneDimensionY / 2;
            const camera = new three_1.OrthographicCamera(-halfCameraWidth, halfCameraWidth, halfCameraHeight, -halfCameraHeight, 1, 1000);
            camera.position.copy(sceneCenter.clone().add(faceNormal.clone().normalize().multiplyScalar(cameraDistance)));
            camera.up.copy((_c = cameraUp.clone().normalize()) !== null && _c !== void 0 ? _c : consts_1.consts.DIRECTIONS_LIST.Y.clone());
            camera.lookAt(sceneCenter);
            // Take screenshot and return
            const image = yield this.takeScreenShot(renderer, scene, camera, addLights);
            canvas.remove();
            renderer.dispose();
            renderer.forceContextLoss();
            return {
                image,
                width: sceneDimensionX,
                height: sceneDimensionY,
            };
        });
    }
    takeOrthogonalScreenShot(scene, faceNormal, cameraUp, settings) {
        var _a, _b, _c;
        return __awaiter(this, void 0, void 0, function* () {
            const backgroundColor = (_a = settings.backgroundColor) !== null && _a !== void 0 ? _a : new three_1.Color(consts_1.consts.WHITE_COLOR);
            const extraScaleFactor = (_b = settings.extraScaleFactor) !== null && _b !== void 0 ? _b : 1;
            const screenShotSize = (_c = settings.size) !== null && _c !== void 0 ? _c : consts_1.consts.SCREENSHOT_SIDE_LENGTH_PX;
            const { addDimensions, convertedFromDPSceneToThreeScene, addLights, isIsometric } = settings;
            // Prepare orthogonal image of scene
            const sceneImage = yield this.prepareSceneImageForOrthogonalScreenShot(scene, faceNormal, cameraUp, {
                addLights,
                backgroundColor,
                size: screenShotSize,
                isIsometric,
            });
            const { image: sceneScreenShot, width: sceneDimensionX, height: sceneDimensionY } = sceneImage;
            const longerDimension = (0, utils_3d_1.largest)([sceneDimensionX, sceneDimensionY]);
            // // Set up renderer
            const canvas = document.createElement('canvas');
            const renderer = new three_1.WebGLRenderer({
                antialias: false,
                canvas,
                preserveDrawingBuffer: true,
            });
            renderer.setSize(screenShotSize, screenShotSize);
            renderer.setClearColor(backgroundColor);
            // Create texture from the screen shot
            const textureLoader = new three_1.TextureLoader();
            const screenShotBlobURL = URL.createObjectURL(sceneScreenShot);
            const screenShotTexture = yield textureLoader.loadAsync(screenShotBlobURL);
            // Create quad
            const planeGeometry = new three_1.PlaneGeometry(sceneDimensionX, sceneDimensionY);
            const planeMaterial = new three_1.MeshBasicMaterial({ map: screenShotTexture, side: three_1.DoubleSide });
            planeMaterial.needsUpdate = true;
            const plane = new three_1.Mesh(planeGeometry, planeMaterial);
            // Scale it using sceneDimensionX and sceneDimensionY to fit the longer side of the scene to renderer size
            let planeScreenShotSideLength = screenShotSize - consts_1.consts.SCREENSHOT_PADDING * 2;
            // Subtract dimension leader size if dimensions are to be added
            if (addDimensions) {
                planeScreenShotSideLength -= DimensionLeaderGenerator_1.DimensionLeaderGenerator.dimensionLeaderHeight;
            }
            const fitToRendererScaleFactor = planeScreenShotSideLength / longerDimension;
            plane.scale.copy(new three_1.Vector3(fitToRendererScaleFactor, fitToRendererScaleFactor, 1));
            // Apply extra scale passed as parameter (needed to make smaller panel size relative to the largest panel)
            if ((0, utils_3d_1.areNotEqual)(extraScaleFactor, 1)) {
                plane.scale.multiply(new three_1.Vector3(extraScaleFactor, extraScaleFactor, 1));
            }
            if (convertedFromDPSceneToThreeScene) {
                // Flip horizontally
                plane.scale.multiply(new three_1.Vector3(-1, 1, 1));
            }
            // Create new scene
            const orthogonalScreenShotScene = new three_1.Scene();
            // Add the quad in XY plane with it's center matching the world origin
            orthogonalScreenShotScene.add(plane);
            const cameraPosition = new three_1.Vector3(0, 0, 5);
            if (addDimensions) {
                // Add the width and height dimension leaders and dimensions to the scene
                const widthLeaderLength = sceneDimensionX * fitToRendererScaleFactor * extraScaleFactor;
                const heightLeaderLength = sceneDimensionY * fitToRendererScaleFactor * extraScaleFactor;
                const getLabelFn = this.viewer.isImperialMeasurementUsed ? utils_1.getLabelInInch : utils_1.getLabelInMM;
                const widthLabelText = getLabelFn(sceneDimensionX);
                const widthLeaderTranslation = heightLeaderLength / 2 + consts_1.consts.SCREENSHOT_DIMENSION_LEADER_END_MARKER_HEIGHT;
                const heightLabelText = getLabelFn(sceneDimensionY);
                const heightLeaderTranslation = widthLeaderLength / 2 + consts_1.consts.SCREENSHOT_DIMENSION_LEADER_END_MARKER_HEIGHT;
                const widthDimensionLeader = this.dimensionLeaderGenerator.generateFromParams({
                    length: widthLeaderLength,
                    labelText: widthLabelText,
                    orientation: 'horizontal',
                });
                // Move width dimension leader to the bottom of the plane
                widthDimensionLeader.translateY(-widthLeaderTranslation);
                const heightDimensionLeader = this.dimensionLeaderGenerator.generateFromParams({
                    length: heightLeaderLength,
                    labelText: heightLabelText,
                    orientation: 'vertical',
                });
                // Move height dimension leader to the left of the plane
                // translateY() because vertical leader is rotated about Z by PI / 2.
                heightDimensionLeader.translateY(heightLeaderTranslation);
                orthogonalScreenShotScene.add(widthDimensionLeader);
                orthogonalScreenShotScene.add(heightDimensionLeader);
                // Set the camera position so that the scene will stick to the bottom of the renderer vertically and center horizontally
                cameraPosition.setX(-(DimensionLeaderGenerator_1.DimensionLeaderGenerator.dimensionLeaderHeight / 2));
                cameraPosition.setY((planeScreenShotSideLength - heightLeaderLength - DimensionLeaderGenerator_1.DimensionLeaderGenerator.dimensionLeaderHeight) / 2);
            }
            // Reset camera configuration as size = renderer size
            const halfRendererSideLength = screenShotSize / 2;
            const camera = new three_1.OrthographicCamera(-halfRendererSideLength, halfRendererSideLength, halfRendererSideLength, -halfRendererSideLength, 1, 1000);
            camera.position.copy(cameraPosition.clone());
            // Set the Y axis as camera up
            camera.up.copy(new three_1.Vector3(0, 1, 0));
            // Set the camera eye to a point on Y axis
            camera.lookAt(new three_1.Vector3(cameraPosition.x, cameraPosition.y, 0));
            // Take the screenshot
            const screenShotWithDimensions = yield this.takeScreenShot(renderer, orthogonalScreenShotScene, camera);
            canvas.remove();
            renderer.dispose();
            renderer.forceContextLoss();
            return screenShotWithDimensions;
        });
    }
    static getOrthographicScreenShotConfig(scene, faceNormal = consts_1.consts.DIRECTIONS_LIST['-Z'].clone(), cameraUp = consts_1.consts.DIRECTIONS_LIST.Y.clone(), isIsometric = false) {
        // Calculate orientation of the bounding box to use new co-ordinate system based on faceNormal and cameraUp
        // Prepare axes of new 3D space based on faceNormal and cameraUp
        const Z = faceNormal.clone().negate().normalize();
        const X = Z.clone().cross(cameraUp).normalize();
        const Y = X.clone().cross(Z).normalize();
        // Calculate matrix to generate new space
        const newSpaceTransform = new three_1.Matrix4();
        newSpaceTransform.set(X.x, Y.x, Z.x, 0, X.y, Y.y, Z.y, 0, X.z, Y.z, Z.z, 0, 0, 0, 0, 1);
        newSpaceTransform.multiply(scene.matrix);
        // Create scene, transform it into new space and copy children
        const sceneInNewSpace = new three_1.Group();
        sceneInNewSpace.matrixAutoUpdate = false;
        sceneInNewSpace.matrix = newSpaceTransform.clone();
        const transformedScene = new three_1.Scene();
        transformedScene.add(sceneInNewSpace);
        scene.children.forEach((item) => {
            sceneInNewSpace.add(item.clone());
        });
        // Create oriented bounding box for the transformed scene using axis-aligned bounding boxes of the original scene and transformed scene
        const originalSceneAABB = new three_1.Box3().setFromObject(scene);
        const transformedSceneAABB = new three_1.Box3().setFromObject(transformedScene);
        // OBB size will be the size of transformed scene
        const transformedSceneDimensions = transformedSceneAABB.getSize(new three_1.Vector3());
        const halfSize = transformedSceneDimensions.divideScalar(2);
        // OBB center will be the center of original scene
        const center = originalSceneAABB.getCenter(new three_1.Vector3());
        // OBB rotation will be the rotation of transformed scene
        // const rotation = matrix3FromQuaternion(newSpace.quaternion);
        const rotation = new three_1.Matrix3().setFromMatrix4(newSpaceTransform.clone().extractRotation(new three_1.Matrix4()));
        const transformedSceneOBB = new OBB_js_1.OBB(center, halfSize, rotation);
        // Calculate OrthographicScreenShotConfig from OBB for transformed scene
        // Remove bounding box thickness to get the scene dimensions
        const sceneDimensions = transformedSceneOBB.getSize(new three_1.Vector3()).subScalar(0.1);
        const sceneCenter = transformedSceneOBB.center.clone();
        // For isometric screen shots the dimensions should be the diameter of the bounding sphere of the OBB of the transformed scene
        if (isIsometric) {
            const boxWithTransformedSceneOBBDimensions = new three_1.Box3().setFromCenterAndSize(new three_1.Vector3(), sceneDimensions.clone());
            const transformedSceneBoundingSphere = boxWithTransformedSceneOBBDimensions.getBoundingSphere(new three_1.Sphere());
            const transformedSceneBoundingSphereDiameter = 2 * transformedSceneBoundingSphere.radius;
            sceneDimensions.set(transformedSceneBoundingSphereDiameter, transformedSceneBoundingSphereDiameter, transformedSceneBoundingSphereDiameter);
        }
        // Calculate camera distance using bounding sphere of transformed scene
        const sceneBoundingSphere = transformedSceneAABB.getBoundingSphere(new three_1.Sphere());
        const cameraDistance = sceneBoundingSphere.radius;
        return {
            sceneDimensions,
            sceneCenter,
            cameraDistance,
        };
    }
    takeScreenShot(renderer, scene, camera, addLights = false) {
        return __awaiter(this, void 0, void 0, function* () {
            return new Promise((resolve) => {
                if (addLights) {
                    // Algorithm:
                    //  1. Find the angle and the axis about which the camera is rotated w.r.t. front top right corner (isometric view camera direction).
                    //  2. Prepare group of lights and add necessary lights to it.
                    //  3. Rotate the group of lights using the angle and axis found in step 1.
                    //  4. Prepare wrapper for the group of lights that follows design pro co-ordinate system and add the lights group to it.
                    //  5. Prepare a scene that follows standard co-ordinate system, add actual scene and lights group wrapper to it and take screen shot.
                    //  1.
                    const cameraDirection = new three_1.Vector3().setFromMatrixPosition(camera.matrixWorld.clone()).normalize();
                    const isometricCameraDirection = consts_1.consts.SCREENSHOT_CAMERA_DIRECTION.clone().normalize();
                    const cameraRotationAxis = isometricCameraDirection.clone().cross(cameraDirection.clone()).normalize();
                    const cameraRotationAngle = isometricCameraDirection.angleTo(cameraDirection);
                    //  2.
                    const lightsGroup = new three_1.Group();
                    lightsGroup.add(...this.lights);
                    //  3.
                    lightsGroup.rotateOnWorldAxis(cameraRotationAxis, cameraRotationAngle);
                    //  4.
                    const lightsGroupWrapper = new three_1.Group();
                    lightsGroupWrapper.matrixAutoUpdate = false;
                    lightsGroupWrapper.matrix = DP_THREE_SCENE_TO_THREE_SCENE_TRANSFORM.clone();
                    lightsGroupWrapper.add(lightsGroup);
                    //  5.
                    const standardSpaceScene = new three_1.Scene();
                    standardSpaceScene.add(lightsGroupWrapper);
                    standardSpaceScene.add(scene);
                    renderer.render(standardSpaceScene, camera);
                    renderer.domElement.toBlob(resolve, IMAGE_BLOB_TYPE, 1);
                    return;
                }
                renderer.render(scene, camera);
                renderer.domElement.toBlob(resolve, IMAGE_BLOB_TYPE, 1);
            });
        });
    }
}
exports.ScreenShotManager = ScreenShotManager;
